// Private to @github-ui/sanitize. NOT exported from package.json — the
// `github-sanitize-lax` Trusted Types policy must only be reachable through
// sanitize() / sanitizeWithPurifier(), so that anything assigned to a
// TrustedHTML sink in CSP-`trusted-types`-enforced contexts has actually
// passed through DOMPurify.
//
// The generic policy mechanism (lazy creation, memoization, duplicate
// tolerance, metrics, and test seams) lives in @github-ui/trusted-types; this
// module only supplies the `github-sanitize-lax` name, flag, and rules and keeps
// the resulting binding private.
//
// Trusted Types policy names:
//   • `github-sanitize-lax`      — the lax fallback.
//   • `github-sanitize-markdown` — the strict `markdown` preset.
//   • `github-sanitize-math`     — the strict `math` preset.
// No strict preset reuses `github-sanitize-lax`. Each strict preset mints through its
// own dedicated policy so its output is distinguishable in CSP `trusted-types`
// telemetry and the configs can evolve independent trust boundaries.
import {createTrustedTypesPolicy} from '@github-ui/trusted-types'

const sanitizePolicy = createTrustedTypesPolicy({
  name: 'github-sanitize-lax',
  createHTML: input => input,
  // DOMPurify requires `createScriptURL` on any policy it consumes via its
  // `TRUSTED_TYPES_POLICY` config; identity is safe because DOMPurify only
  // forwards already-validated URLs to this hook.
  createScriptURL: input => input,
})

// The `markdown` preset mints through its own dedicated policy. NOTE: the preset's
// public arg value is the short `'markdown'`; this policy NAME is intentionally the
// fully-qualified `github-sanitize-markdown`.
const markdownSanitizePolicy = createTrustedTypesPolicy({
  name: 'github-sanitize-markdown',
  createHTML: input => input,
  createScriptURL: input => input,
})

// The `math` preset mints through its own dedicated policy. Same short-arg /
// fully-qualified-name split as markdown: the preset arg is `'math'`, the policy
// NAME is `github-sanitize-math`.
const mathSanitizePolicy = createTrustedTypesPolicy({
  name: 'github-sanitize-math',
  createHTML: input => input,
  createScriptURL: input => input,
})

/**
 * Lazily create and memoize the `github-sanitize-lax` Trusted Types policy.
 * Returns null when Trusted Types are unavailable, the feature flag is
 * off, or the policy could not be created.
 */
export const getSanitizePolicy = sanitizePolicy.getPolicy

/**
 * Wrap an already-sanitized HTML string as a `TrustedHTML` when the
 * `github-sanitize-lax` policy is active. Falls back to returning the input
 * branded as `SafeHTMLString` when Trusted Types are unavailable or the
 * policy could not be created.
 *
 * The input MUST already be DOMPurify output — this is what justifies
 * branding the fallback. Keep this helper package-private.
 */
export const mintTrustedHTML = sanitizePolicy.mint

/**
 * Lazily create and memoize the `github-sanitize-markdown` Trusted Types policy
 * used by the `markdown` preset. Same semantics as {@link getSanitizePolicy}, but
 * a distinct policy binding.
 */
export const getMarkdownSanitizePolicy = markdownSanitizePolicy.getPolicy

/**
 * Wrap an already-sanitized HTML string (markdown-preset DOMPurify output) as a
 * `TrustedHTML` when the `github-sanitize-markdown` policy is active. Same
 * fallback semantics as {@link mintTrustedHTML}. Keep package-private.
 */
export const mintMarkdownTrustedHTML = markdownSanitizePolicy.mint

/**
 * Lazily create and memoize the `github-sanitize-math` Trusted Types policy used by
 * the `math` preset. Same semantics as {@link getSanitizePolicy}, but a distinct
 * policy binding.
 */
export const getMathSanitizePolicy = mathSanitizePolicy.getPolicy

/**
 * Wrap an already-sanitized HTML string (math-preset DOMPurify output) as a
 * `TrustedHTML` when the `github-sanitize-math` policy is active. Same fallback
 * semantics as {@link mintTrustedHTML}. Keep package-private.
 */
export const mintMathTrustedHTML = mathSanitizePolicy.mint

/**
 * Reset the memoized policy. Test-only — not part of the public package API.
 */
export const _resetSanitizePolicyForTests = sanitizePolicy._resetForTests

/**
 * Reset the memoized markdown policy. Test-only — not part of the public package API.
 */
export const _resetMarkdownSanitizePolicyForTests = markdownSanitizePolicy._resetForTests

/**
 * Reset the memoized math policy. Test-only — not part of the public package API.
 */
export const _resetMathSanitizePolicyForTests = mathSanitizePolicy._resetForTests

/**
 * Inject a fake `TrustedTypePolicyFactory` (or `null` to simulate the
 * platform global being absent). Test-only — pass `undefined` to revert
 * to the real platform global. Applies to every preset policy so tests can
 * drive the whole module through one seam.
 */
export const _setTrustedTypesForTests = (impl: Parameters<typeof sanitizePolicy._setTrustedTypesForTests>[0]) => {
  sanitizePolicy._setTrustedTypesForTests(impl)
  markdownSanitizePolicy._setTrustedTypesForTests(impl)
  mathSanitizePolicy._setTrustedTypesForTests(impl)
}
